'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Sun, Leaf, Snowflake, Calendar, Clock, CheckCircle } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import { maintenanceScheduleTranslations } from '@/translations/maintenanceSchedule'
import { Listbox } from '@headlessui/react'
import { ChevronDown } from 'lucide-react'

export default function MaintenanceSchedule() {
  const { language } = useLanguage()
  const t = maintenanceScheduleTranslations[language]
  const [selectedSeason, setSelectedSeason] = useState(t.seasons[0])

  // Update selectedSeason when language changes
  useEffect(() => {
    setSelectedSeason(t.seasons[0])
  }, [language, t.seasons])

  return (
    <section id="maintenance" className="py-20 bg-gradient-to-br from-white to-teal-50/15">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 text-sharp"
          >
            {t.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl text-gray-600 leading-relaxed text-sharp"
          >
            {t.subtitle}
          </motion.p>
        </div>

        {/* Season Selector */}
        {/* Desktop: Button group */}
        <div className="hidden lg:flex flex-wrap justify-center gap-4 mb-12">
          {t.seasons.map((season, index) => (
            <motion.button
              key={season.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              onClick={() => setSelectedSeason(season)}
              className={`px-6 py-4 rounded-xl font-medium transition-all duration-300 border focus:outline-none ${
                selectedSeason === season
                  ? 'bg-gray-900 text-white border-gray-900 shadow-md hover:shadow-lg'
                  : 'bg-white text-gray-500 border-gray-200 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <span>{season.name}</span>
            </motion.button>
          ))}
        </div>
        {/* Mobile/Tablet: Dropdown */}
        <div className="lg:hidden mb-8 relative">
          <Listbox value={selectedSeason.name} onChange={name => {
            const found = t.seasons.find(s => s.name === name)
            if (found) setSelectedSeason(found)
          }}>
            {({ open }) => (
              <div>
                <Listbox.Button className={`w-full px-6 py-4 text-left bg-white rounded-lg shadow-sm focus:outline-none transition-all duration-200 border ${open ? 'border-gray-500 ring-2 ring-gray-200' : 'border-gray-300'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium text-gray-900">{selectedSeason.name}</span>
                    <ChevronDown className="h-5 w-5 text-gray-600" aria-hidden="true" />
                  </div>
                </Listbox.Button>
                <Listbox.Options className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-sm border border-gray-100 max-h-60 overflow-auto focus:outline-none">
                  {t.seasons.map(season => (
                    <Listbox.Option
                      key={season.name}
                      value={season.name}
                      className={({ active }) =>
                        `relative cursor-pointer select-none px-6 py-4 ${active ? 'bg-gray-50 text-gray-900' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`
                      }
                    >
                      {({ selected }) => (
                        <span className={`block text-lg font-medium ${selected ? 'text-gray-900' : 'text-gray-500'}`}>{season.name}</span>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            )}
          </Listbox>
        </div>

        {/* Maintenance Content */}
        <div
          className="grid lg:grid-cols-2 gap-12 items-start"
        >
          {/* Tasks List */}
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
            <motion.div
              key={selectedSeason.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  {(() => {
                    const IconComponent = getSeasonIcon(selectedSeason.name)
                    return <IconComponent className="w-6 h-6 text-gray-900" />
                  })()}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 text-sharp">
                    {selectedSeason.name}
                  </h3>
                  <p className="text-gray-600 text-sharp">
                    {selectedSeason.months.join(', ')}
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                {selectedSeason.tasks.map((taskGroup, groupIndex) => (
                  <div key={taskGroup.category} className="space-y-4">
                    <h4 className="font-semibold text-gray-900 text-sharp">
                      {taskGroup.category}
                    </h4>
                    <ul className="space-y-3">
                      {taskGroup.items.map((item, index) => (
                        <motion.li
                          key={item}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5, delay: (groupIndex * 0.1) + (index * 0.05) }}
                          className="flex items-start gap-3 text-gray-700 text-sharp"
                        >
                          <CheckCircle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Benefits & Tips */}
          <div className="space-y-6">
            <div className="bg-white rounded-3xl p-6 shadow-xl border border-gray-100">
              <h4 className="text-xl font-bold text-gray-900 mb-4 text-sharp">
                {language === 'en' ? 'Why Regular Maintenance?' : 'Pam Cynnal a Chadw Rheolaidd?'}
              </h4>
              <ul className="space-y-3">
                {[
                  language === 'en' ? 'Keeps your garden healthy and thriving' : 'Cadw eich gardd yn iach a ffyniannus',
                  language === 'en' ? 'Prevents problems before they start' : 'Atal problemau cyn iddynt ddechrau',
                  language === 'en' ? 'Saves money in the long run' : 'Arbed arian yn y tymor hir',
                  language === 'en' ? 'Increases your property value' : 'Cynyddu gwerth eich eiddo'
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700 text-sharp leading-relaxed">
                    <div className="w-2 h-2 bg-gray-600 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
              <h4 className="text-xl font-bold text-gray-900 mb-4 text-sharp">
                {language === 'en' ? 'Pro Tips' : 'Cyngor Proffesiynol'}
              </h4>
              <p className="text-gray-700 mb-4 text-sharp leading-relaxed">
                {language === 'en' 
                  ? 'Regular maintenance is key to a beautiful garden. I can help you stay on schedule.'
                  : 'Mae cynnal a chadw rheolaidd yn allweddol i gardd brydferth. Gall ein tîm arbenigol eich helpu i aros ar amserlen.'
                }
              </p>
              <div className="flex items-center gap-2 text-gray-900 font-medium text-sharp">
                <Clock className="w-4 h-4" />
                <span>{language === 'en' ? 'Weekly visits available' : 'Ymweliadau wythnosol ar gael'}</span>
              </div>
            </div>

            {/* Maintenance Checklist - Replaces redundant CTA */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-6 text-white">
              <h4 className="text-xl font-bold text-white mb-4 text-sharp">
                {language === 'en' ? 'Quick Maintenance Checklist' : 'Rhestr Wirio Cynnal a Chadw'}
              </h4>
              <div className="space-y-3 mb-4">
                {[
                  language === 'en' ? 'Check soil moisture levels' : 'Gwirio lefelau lleithder pridd',
                  language === 'en' ? 'Remove weeds and dead plants' : 'Tynnu chwyn a phlanhigion marw',
                  language === 'en' ? 'Inspect for pests and diseases' : 'Archwilio am blâu a chlefydau',
                  language === 'en' ? 'Prune overgrown branches' : 'Torri canghennau gor-gynhyrchiol'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full flex-shrink-0"></div>
                    <span className="text-gray-200 text-sm text-sharp leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
              <div className="text-center pt-3 border-t border-gray-700">
                <p className="text-emerald-400 font-medium text-sm">
                  {language === 'en' 
                    ? "Need professional help? Contact us below"
                    : "Angen cymorth proffesiynol? Cysylltwch â ni isod"
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function getSeasonIcon(seasonName: string) {
  switch (seasonName.toLowerCase()) {
    case 'spring':
    case 'gwanwyn':
      return Sun
    case 'summer':
    case 'haf':
      return Sun
    case 'autumn':
    case 'hydref':
      return Leaf
    case 'winter':
    case 'gaeaf':
      return Snowflake
    default:
      return Calendar
  }
} 